import React, { createContext, useState, useContext, useEffect } from 'react';
import * as SecureStore from 'expo-secure-store';
import { AuthContext } from './AuthContext';

export const ClientContext = createContext();

export const ClientProvider = ({ children }) => {
  const { userData } = useContext(AuthContext);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientList, setClientList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Helper function to interact with storage (SecureStore or localStorage)
  const getStorageItem = async (key) => {
    try {
      // Check if we're in a web environment
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      } else {
        return await SecureStore.getItemAsync(key);
      }
    } catch (error) {
      console.error(`Error getting ${key} from storage:`, error);
      return null;
    }
  };

  const setStorageItem = async (key, value) => {
    try {
      // Check if we're in a web environment
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      } else {
        await SecureStore.setItemAsync(key, value);
      }
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
    }
  };

  // Update client list when userData changes
  useEffect(() => {
    //console.log('ClientContext: userData changed:', userData);
    
    if (userData) {
      // Handle both single client and multiple client scenarios
      let clients = [];
      
      if (userData.clientAccess && Array.isArray(userData.clientAccess)) {
        clients = userData.clientAccess;
      } else if (userData.clientId && userData.clientName) {
        // Single client scenario from login response
        clients = [{
          clientId: userData.clientId,
          clientName: userData.clientName
        }];
      }
      
      //console.log('ClientContext: Available clients:', clients);
      setClientList(clients);
      
      // Check if a client was previously selected
      const loadSelectedClient = async () => {
        try {
          const savedClientId = await getStorageItem('selectedClientId');
          
          if (savedClientId && clients.length > 0) {
            const clientId = parseInt(savedClientId);
            const client = clients.find(c => c.clientId === clientId);
            
            if (client) {
             // console.log('ClientContext: Restored saved client:', client);
              setSelectedClient(client);
            } else {
              // If saved client not found, select first available
              //console.log('ClientContext: Auto-selecting first client:', clients[0]);
              setSelectedClient(clients[0]);
              await setStorageItem('selectedClientId', clients[0].clientId.toString());
            }
          } else if (clients.length > 0) {
            // If no previously selected client, select first available
            //console.log('ClientContext: Auto-selecting first client:', clients[0]);
            setSelectedClient(clients[0]);
            await setStorageItem('selectedClientId', clients[0].clientId.toString());
          }
        } catch (error) {
          console.error('Error loading selected client:', error);
        } finally {
          setIsLoading(false);
        }
      };
      
      loadSelectedClient();
    } else {
      console.log('ClientContext: No userData, clearing clients');
      setClientList([]);
      setSelectedClient(null);
      setIsLoading(false);
    }
  }, [userData]);

  // Change selected client
  const selectClient = async (client) => {
    setSelectedClient(client);
    try {
      await setStorageItem('selectedClientId', client.clientId.toString());
    } catch (error) {
      console.error('Error saving selected client:', error);
    }
  };

  return (
    <ClientContext.Provider
      value={{
        selectedClient,
        clientList,
        isLoading,
        selectClient,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};